// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { CommunityGuidelinesTelemetryObjects, communityGuidelinesTelemetryActionDispatchers, communityGuidelinesTelemetryContracts } from "./CommunityGuidelinePage.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../Social.telemetry";
import { guidlineImages, logoImages } from "../utils/images";

import Navbar from "../common/navbar/Navbar";
import React from "react";
import { SocialProps } from "../Social.props";
import { String } from "typescript-string-operations";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../utils/enums";
import { getContextData } from "@msnews/core";
import { hasThemeBase } from "../utils/themeHelpers";
import { renderToString } from "react-dom/server";

const guidelineTabs = {
    Principles: "Principles",
    Guidelines: "Guidelines",
    Enforcement: "Enforcement"
};

interface CommunityGuidelinePageState {
    activeTab: string;
}

export default class CommunityGuidelinePage extends React.Component<SocialProps, CommunityGuidelinePageState> {
    telemetryObjects: CommunityGuidelinesTelemetryObjects;

    constructor(props) {
        super(props);

        this.state = {
            activeTab: null
        };
    }

    componentDidMount() {
        const requestContext = this.props.requestContext;
        const locationHref = getContextData(requestContext.data.locationHref, "locationHref");
        const tabNameFromUrl = Object.keys(guidelineTabs).filter(key => locationHref.toLowerCase().endsWith(key.toLowerCase())).pop();
        this.setState({ activeTab: tabNameFromUrl || guidelineTabs.Principles});
        this.props.markTtvr();
    }

    showTab(e, tab, telemetryObject?) {
        this.setState({
            activeTab: tab
        });

        if (telemetryObject) {
            communityGuidelinesTelemetryActionDispatchers.genericClicked(e.currentTarget, telemetryObject);
        }
    }

    navbarBackHandler() {
        if (history.state.initial) {
            this.props.popupConnector.closePopup();
        } else {
            history.back();
        }
    }

    render() {
        const {responsiveClass, t, mainTelemetryObject} = this.props;
        const {activeTab} = this.state;
        const isMobile = responsiveClass.includes(enums.responsiveLevels.L0);
        const mainTitle =  isMobile ? t[`CommunityGuidelinePageTabs${activeTab}`] : t.OnboardingProceedingGuidelines;
        const isSuperApp = hasThemeBase(this.props.theme, enums.theme.superappBase);

        /** Generate Telemetry Objects */
        this.telemetryObjects = {
            tabs: {},
            guidelinesBtnAtPrinciples: addOrUpdateTelemetryObject(mainTelemetryObject, communityGuidelinesTelemetryContracts.guidelinesBtn, {}, this.telemetryObjects && this.telemetryObjects.guidelinesBtnAtPrinciples),
            guidelinesBtnAtEnforcement: addOrUpdateTelemetryObject(mainTelemetryObject, communityGuidelinesTelemetryContracts.guidelinesBtn, {}, this.telemetryObjects && this.telemetryObjects.guidelinesBtnAtEnforcement),
            enforcementBtn: addOrUpdateTelemetryObject(mainTelemetryObject, communityGuidelinesTelemetryContracts.enforcementBtn, {}, this.telemetryObjects && this.telemetryObjects.enforcementBtn),
            principlesBtn: addOrUpdateTelemetryObject(mainTelemetryObject, communityGuidelinesTelemetryContracts.principlesBtn, {}, this.telemetryObjects && this.telemetryObjects.principlesBtn)
        };

        return (
            <div className={classNames(
                this.props.managedClasses.communityGuidelinePageContainer,
                this.props.responsiveClass,
                [this.props.managedClasses.communityGuidelinePageSuperAppContainer, isSuperApp]
            )}>
                { isSuperApp &&
                    <Navbar
                        {...{...this.props }}
                        onBackClick={() => this.navbarBackHandler()}
                    />
                }

                <header className="cwt-main-header cwt-main-page-header">
                    <div className="cwt-section cwt-title-section">
                        <div className="cwt-image-title">
                            <img src={logoImages.msNews} alt={t.ImgAltsMicrosoftNewsLogo} className="cwt-image"/>
                            <h1 className="cwt-sub-title">{t.GuidelinesPageSubHeading}</h1>
                        </div>
                        <h2 className="cwt-title">{ mainTitle }</h2>
                    </div>
                    <div className="cwt-tabs-container">
                        <ul role="tablist" className="cwt-tabs cwt-profile-tabs no-select cwt-section">
                            { Object.keys(guidelineTabs).map(key => {
                                const tab = guidelineTabs[key];
                                this.telemetryObjects.tabs[key] = addOrUpdateTelemetryObject(mainTelemetryObject, communityGuidelinesTelemetryContracts.tabs[key], {}, this.telemetryObjects && this.telemetryObjects.tabs[key]);

                                return (

                                    <li role="tab" key={tab} className={`${activeTab == tab && "active"} cwt-tab-item cwt-link`} aria-selected={activeTab == tab} onClick={(e) => this.showTab(e, tab, this.telemetryObjects.tabs[key])}
                                        {...renderHybrid(this.telemetryObjects.tabs[key], false)}
                                    >
                                        <i className="cwt-icon">
                                            <span className="cwt-icon-label">{t[`CommunityGuidelinePageTabs${tab}`]}</span>
                                        </i>
                                    </li>
                                );
                            }
                            )}
                        </ul>
                    </div>
                </header>

                <div className="cwt-tab-content">
                    { activeTab == guidelineTabs.Guidelines &&
                        <React.Fragment>
                            <ul className="cwt-section cwt-section-list">
                                <li className="cwt-guideline-section">
                                    <img src={guidlineImages.g2} alt={t.ImgAltsPeopleReactingContents} className="cwt-image"/>
                                    <div className="cwt-content">
                                        <p className="cwt-text">
                                            {t.GuidelinesPageGuidelinesTabAvoidPartText}
                                        </p>
                                        <ul className="cwt-subject-list">
                                            <li>{t.GuidelinesPageGuidelinesTabAvoidPartHarassment}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabAvoidPartBullying}</li>
                                        </ul>
                                    </div>
                                </li>

                                <li className="cwt-guideline-section">
                                    <img src={guidlineImages.g3} alt={t.ImgAltsPeopleSendingComments} className="cwt-image"/>
                                    <div className="cwt-content">
                                        <p className="cwt-text">
                                            {t.GuidelinesPageGuidelinesTabRespectPartText}
                                        </p>
                                        <ul className="cwt-subject-list">
                                            <li>{t.GuidelinesPageGuidelinesTabRespectPartImpersonate}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabRespectPartRespectOthers}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabRespectPartNotShareContent}</li>
                                        </ul>
                                    </div>
                                </li>

                                <li className="cwt-guideline-section">
                                    <img src={guidlineImages.g4} alt={t.ImgAltsTwoPeopleInteractingSocialUI} className="cwt-image"/>
                                    <div className="cwt-content">
                                        <p className="cwt-text">
                                            {t.GuidelinesPageGuidelinesTabReportPartText}
                                        </p>
                                        <ul className="cwt-subject-list">
                                            <li>{t.GuidelinesPageGuidelinesTabReportPartViolent}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabReportPartIllegalAct}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabReportPartRiskTo}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabReportPartProfaneText}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabReportPartHateSpeech}</li>
                                        </ul>
                                    </div>
                                </li>

                                <li className="cwt-guideline-section">
                                    <img src={guidlineImages.g5} alt={t.ImgAltsTwoPeopleShakingHands} className="cwt-image"/>
                                    <div className="cwt-content">
                                        <p className="cwt-text">
                                            {t.GuidelinesPageGuidelinesTabFinalPartText}
                                        </p>
                                        <ul className="cwt-subject-list">
                                            <li>{t.GuidelinesPageGuidelinesTabFinalPartCopywrited}</li>
                                            <li>{t.GuidelinesPageGuidelinesTabFinalPartSpam}</li>
                                        </ul>
                                    </div>
                                </li>

                            </ul>

                            <div className="cwt-section cwt-inner-nav">
                                <div className="cwt-buttons">
                                    <OutlineButton className="cwt-fast-button cwt-fast-button-outline" onClick={(e) => this.showTab(e, guidelineTabs.Principles)} {...renderHybrid(this.telemetryObjects.principlesBtn, false)}> {t.CommunityGuidelinePageTabsPrinciples} </OutlineButton>
                                    <AccentButton className="cwt-fast-button" onClick={(e) => this.showTab(e, guidelineTabs.Enforcement)} {...renderHybrid(this.telemetryObjects.enforcementBtn, false)}> {t.CommunityGuidelinePageTabsEnforcement} </AccentButton>
                                </div>
                            </div>
                        </React.Fragment>
                    }

                    { activeTab == guidelineTabs.Principles &&
                        <div className="cwt-section cwt-guideline-detail-container">
                            <div className="cwt-principles-detail cwt-content">
                                <p className="cwt-text">
                                    {t.GuidelinesPagePrinciplesTabSupportText}
                                </p>
                                <p className="cwt-text">
                                    {t.GuidelinesPagePrinciplesTabUpholdText}
                                </p>
                                <p className="cwt-text">
                                    {t.GuidelinesPagePrinciplesTabRespectLawText}
                                </p>
                                <p className="cwt-text" dangerouslySetInnerHTML={{ __html: String.Format(t.GuidelinesPagePrinciplesTabInformText,
                                    {
                                        CodeofConduct: renderToString(<a href="https://www.microsoft.com/en-us/servicesagreement" target="_blank">{t.CodeOfConduct}</a>),
                                        MicrosoftServicesAgreement: renderToString(<a href="https://www.microsoft.com/en-us/servicesagreement" target="_blank">{t.MicrosoftServicesAgreement}</a>)
                                    })
                                }}/>
                                <div className="cwt-section cwt-inner-nav">
                                    <div className="cwt-buttons">
                                        <AccentButton className="cwt-fast-button" onClick={(e) => this.showTab(e, guidelineTabs.Guidelines)} {...renderHybrid(this.telemetryObjects.guidelinesBtnAtPrinciples, false)}> {t.CommunityGuidelinePageTabsGuidelines} </AccentButton>
                                    </div>
                                </div>
                            </div>
                            <img src={guidlineImages.g1} alt={t.ImgAltsPeopleReactingContents} className="cwt-image"/>
                        </div>
                    }

                    { activeTab == guidelineTabs.Enforcement &&
                        <div className="cwt-section cwt-guideline-detail-container">
                            <div className="cwt-enforcement-detail cwt-content">
                                <p className="cwt-text">
                                    {t.GuidelinesPageEnforcementTabFlaggedText}
                                </p>
                                <p className="cwt-text">
                                    {t.GuidelinesPageEnforcementTabSupendText}
                                </p>
                                <p className="cwt-text">
                                    {t.GuidelinesPageDeemedViolating}
                                </p>
                                <div className="cwt-section cwt-inner-nav">
                                    <div className="cwt-buttons">
                                        <AccentButton className="cwt-fast-button" onClick={(e) => this.showTab(e, guidelineTabs.Guidelines)} {...renderHybrid(this.telemetryObjects.guidelinesBtnAtEnforcement, false)}> {t.CommunityGuidelinePageTabsGuidelines} </AccentButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }
}